import axios, { AxiosError } from "axios";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import AcedInput from "../../../components/essentials/input/text/AcedInput";
import { setUser } from "../../../core/auth/authSlice";
import { addErrorMessage } from "../../../core/errors/errorSlice";
import { PasswordApiRoutes } from "../../../shared/api-routes/auth/password-api.routes";
import { EMAIL_PATTERN } from "../../../shared/const";
import http from "../../../shared/utils/http";
import "./SignUpEmail.scss";

const SignUpEmail: React.FC = () => {
	const {
		control,
		handleSubmit,
		formState: { errors, isDirty },
		register,
	} = useForm({
		mode: "onChange",
		defaultValues: {
			email: "",
			dob: "",
		},
	});

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const appDispatch = useAppDispatch();
	const { t } = useTranslation();
	const location = useLocation();

	const recoverPasswordScenario: boolean =
		location.pathname.includes("password-recovery");

	const validateDob: (arg0: string) => string | undefined = (date: string) => {
		const dob = new Date(date);
		const now = new Date();

		// Get the difference in years
		let age = now.getFullYear() - dob.getFullYear();

		// Adjust age if the current date is before the birthday this year
		if (now < new Date(now.getFullYear(), dob.getMonth(), dob.getDate())) {
			age--;
		}
		return age >= 14 ? undefined : t("signUp.emailSection.errors.invalidDob");
	};

	const addDob: () => any = () => {
		if (!recoverPasswordScenario)
			return (
				<AcedInput
					placeholder={t("signUp.emailSection.inputPlaceHolderDob")}
					control={control}
					name="dob"
					rules={{
						required: t("signUp.emailSection.errors.required"),
						validate: (value) => validateDob(value),
					}}
					type="date"
					max={new Date().toISOString().split("T")[0]}
				/>
			);
	};
	// const isNumberValid = (_phone: any) => {
	//   return /([\+]\d{1,2})?[-\s\.]?[(]?\d{3}[)]?[-\s\.]?\d{3}[-\s\.]?\d{4}/.test(
	//     _phone
	//   );
	// };

	const onSubmit = (data: any) => {
		const requestPath: string = recoverPasswordScenario
			? PasswordApiRoutes.REQUEST_PASSWORD_RECOVER
			: `/auth/register`;

		const requestBody = recoverPasswordScenario
			? {
					email: data.email,
			  }
			: {
					email: data.email,
					dob: data.dob,
			  };

		http
			.post(requestPath, requestBody, {
				suppressErrorToast: true,
			} as any)
			.then((response) => {
				if (!recoverPasswordScenario) {
					dispatch(
						setUser({
							email: data.email,
							userId: response.id,
							accessToken: response.access_token,
						})
					);
					navigate("/sign-up/code");
				} else {
					navigate("/sign-up/password-recovery/confirm");
				}
			})
			.catch((error) => {
				if (axios.isAxiosError(error)) {
					const axiosError = error as AxiosError<{ message: string }>;
					const errorMessage = axiosError.response?.data.message;

					switch (errorMessage) {
						case "EMAIL_NOT_FOUND":
							appDispatch(
								addErrorMessage({
									title: t("signUp.errors.emailNotFound", "Email Not Found"),
								})
							);
							break;
						case "MEMBER_ALREADY_EXISTS":
							appDispatch(
								addErrorMessage({
									title: t(
										"signUp.errors.memberExists",
										"An account may already exist with this email."
									),
								})
							);
							break;
						default:
							// Handle any other errors
							appDispatch(
								addErrorMessage({
									title: t(
										"signUp.errors.generic",
										"An error occurred. Please try again."
									),
								})
							);
					}
				} else {
					// Handle non-axios errors
					appDispatch(
						addErrorMessage({
							title: t(
								"signUp.errors.generic",
								"An error occurred. Please try again."
							),
						})
					);
				}
			});
	};

	return (
		<div className="sign-up-email">
			<p className="sign-up-email__caption">
				{recoverPasswordScenario
					? t("signUp.recoverPassword.emailTitle")
					: t("signUp.emailSection.title")}
			</p>

			<form
				id="sign-up-email-form"
				className="sign-up-email__input"
				onSubmit={handleSubmit(onSubmit)}
			>
				<AcedInput
					placeholder={t("signUp.emailSection.inputPlaceHolderEmail")}
					control={control}
					name="email"
					rules={{
						required: t("signUp.emailSection.errors.required"),
						pattern: {
							value: EMAIL_PATTERN,
							message: t("signUp.emailSection.errors.pattern"),
						},
					}}
				/>

				{addDob()}
			</form>
		</div>
	);
};

export default SignUpEmail;
